import React, { useState, useEffect, useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fmtUsd, fmtSigned } from "@/lib/journal";
import { Trophy } from "@phosphor-icons/react";

const ROWS = [
  ["Total PnL", (p) => fmtSigned(p.total_pnl), true],
  ["Realized", (p) => fmtSigned(p.realized_pnl), true],
  ["Unrealized", (p) => fmtSigned(p.unrealized_pnl), true],
  ["ROI", (p) => `${p.roi_pct >= 0 ? "+" : ""}${p.roi_pct}%`, true],
  ["Win Rate", (p) => `${p.win_rate}%`, false],
  ["Open Value", (p) => fmtUsd(p.open_market_value), false],
  ["Cost Basis", (p) => fmtUsd(p.cost_basis), false],
  ["Trades", (p) => `${p.total_trades} (${p.open_trades} open)`, false],
  ["Best Trade", (p) => fmtSigned(p.best_trade), false],
  ["Worst Trade", (p) => fmtSigned(p.worst_trade), false],
  ["Top Holding", (p) => p.top_holding || "—", false],
];

export const ComparePanel = ({ portfolios }) => {
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (portfolios.length < 2) return;
    setLeft((l) => l || portfolios[0].id);
    setRight((r) => r || portfolios[1].id);
  }, [portfolios]);

  useEffect(() => {
    if (!left || !right || left === right) { setData(null); return; }
    api.get("/analytics/compare", { params: { ids: `${left},${right}` } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [left, right]);

  const picker = (value, onChange, testid) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 rounded-none border-[#1F1F1F] bg-[#050505] text-xs">
        <SelectValue placeholder="Portfolio" />
      </SelectTrigger>
      <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
        {portfolios.map((p) => (
          <SelectItem key={p.id} value={p.id} data-testid={`${testid}-${p.id}`}>{p.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const cols = useMemo(() => data?.portfolios || [], [data]);

  if (portfolios.length < 2) return null;

  return (
    <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="compare-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Portfolio Compare</h3>
        <div className="flex gap-2">
          {picker(left, setLeft, "compare-left-select")}
          <span className="self-center font-mono text-xs text-zinc-600">vs</span>
          {picker(right, setRight, "compare-right-select")}
        </div>
      </div>

      {left === right ? (
        <p className="mt-8 border border-dashed border-[#1F1F1F] px-4 py-10 text-center text-sm text-zinc-600">
          Pick two different portfolios to compare them.
        </p>
      ) : !data ? (
        <p className="mt-6 font-mono text-sm text-zinc-600">Loading comparison…</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[420px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[#1F1F1F] text-xs uppercase tracking-wider text-zinc-500">
                <th className="py-3 text-left font-medium">Metric</th>
                {cols.map((p) => (
                  <th key={p.portfolio_id} className="py-3 text-right font-medium" data-testid={`compare-col-${p.portfolio_id}`}>
                    <span className={p.portfolio_id === data.leader_id ? "text-[#C58361]" : "text-zinc-300"}>
                      {p.name}
                    </span>
                    {p.portfolio_id === data.leader_id && (
                      <Trophy size={11} className="ml-1.5 inline text-[#C58361]" weight="fill" />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, render, colour]) => {
                const values = cols.map((p) => render(p));
                return (
                  <tr key={label} className="border-b border-[#1F1F1F]" data-testid={`compare-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <td className="py-3 text-zinc-500">{label}</td>
                    {cols.map((p, i) => (
                      <td key={p.portfolio_id}
                          className={`py-3 text-right font-mono ${
                            colour
                              ? values[i].startsWith("-") ? "text-[#EF4444]" : "text-[#4ADE80]"
                              : "text-zinc-300"
                          }`}>
                        {values[i]}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
